"use client";
import { CartContext } from "@repo/contexts/CartContext";
import { useSnackbarHandler } from "@repo/lib/hooks/common";
import {
  useTrackCouponApplied,
  useTrackCouponCodeFailure,
} from "@repo/lib/hooks/webengage";
import { validation } from "@repo/lib/utils";
import { applyCoupon, removeCoupon } from "@repo/server-actions/cart";
import { Button, ErrorMessage, Input, LoadingDots } from "@repo/ui";
import cx from "classnames";
import { useLocale, useTranslations } from "next-intl";
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  useTransition,
} from "react";

const PromoCode = ({ coupon, session, isAutoApply }) => {
  const t = useTranslations("Cart");
  const tCommon = useTranslations("Common");
  const locale = useLocale();
  const { setCartTotalsInfo } = useContext(CartContext);
  const [formValues, setFormValues] = useState(() => ({
    promoCode: !isAutoApply ? coupon || "" : "",
  }));
  const [formerror, setFormerror] = useState({});
  const [formSuccess, setformSuccess] = useState(null);
  const [isPending, startTransition] = useTransition();
  const { showSnackbar } = useSnackbarHandler();
  const { trackCouponAppliedEvent } = useTrackCouponApplied();
  const { trackCouponCodeFailureEvent } = useTrackCouponCodeFailure();

  const handleValidation = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    setFormerror((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  }, []);

  const handleApplyCoupon = async () => {
    startTransition(async () => {
      const response = await applyCoupon(formValues.promoCode, session, locale);
      if (response?.success) {
        setformSuccess(response.message);
        setCartTotalsInfo(response);
        trackCouponAppliedEvent(response?.cartTotal, response?.couponName);
      } else {
        setFormerror({
          promoCode: response?.errorMessage,
        });
        setformSuccess(null);
        trackCouponCodeFailureEvent(formValues.promoCode);
      }
      showSnackbar(response);
    });
  };

  const handleRemoveCoupon = async () => {
    startTransition(async () => {
      const response = await removeCoupon(formValues.promoCode, locale);
      if (response?.success) {
        setformSuccess(response.message);
        setFormValues({
          promoCode: "",
        });
        setCartTotalsInfo(response);
      } else {
        setFormerror({
          promoCode: response?.errorMessage,
        });
        setformSuccess(null);
      }
      showSnackbar(response);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validation(formValues, tCommon);
    if (Object.keys(errors).length === 0) {
      if (coupon && !isAutoApply) {
        handleRemoveCoupon();
      } else {
        handleApplyCoupon();
      }
    } else {
      setFormerror(errors);
      console.log("Failed to submit the form, please check the form fields");
    }
  };

  useEffect(() => {
    if (!isAutoApply) {
      setFormValues({
        promoCode: coupon || "",
      });
    }
  }, [coupon, isAutoApply]);

  useEffect(() => {
    if (formSuccess) {
      const timer = setTimeout(() => {
        setformSuccess(null);
      }, 3000); // 3 seconds

      return () => clearTimeout(timer);
    }
  }, [formSuccess]);

  return (
    <form className="px-3 py-4" onSubmit={handleSubmit}>
      <div className="relative pb-2">
        <Input
          type="text"
          placeholder={t("Enter_Your_Coupon_Code")}
          name="promoCode"
          value={formValues.promoCode}
          onChange={handleValidation}
          readOnly={!isAutoApply && coupon}
          disabled={isPending}
          className={cx(
            "placeholder:text-miniCart-promocode-placeholder pe-28 read-only:bg-white",
            {
              //"!border-mediumgreen": formValues.promoCode, TODO: Remove it after testing
              "!text-miniCart-promocode-couponSuccessText border-miniCart-promocode-couponSuccessText bg-miniCart-promocode-couponSuccessBg":
                !isAutoApply && coupon,
              "!border-miniCart-promocode-couponFailureText text-miniCart-promocode-couponFailureText bg-miniCart-promocode-couponFailureBg":
                formerror?.promoCode,
            }
          )}
        />
        <Button
          size="md"
          type="submit"
          variant="primary"
          className={cx(
            "!text-miniCart-promocode-buttonText absolute end-0 top-0 flex items-center border-none !bg-transparent !px-3",
            {
              "!md:w-[108px]": isPending,
            }
          )}
          disabled={isPending}
        >
          {isPending ? (
            <LoadingDots className="bg-black" />
          ) : (
            <span>
              {!isAutoApply && coupon ? t("Remove_Coupon") : t("Apply_Coupon")}
            </span>
          )}
        </Button>
      </div>
      <ErrorMessage message={formerror?.promoCode} />
      <p
        className={cx(
          "text-miniCart-promocode-couponSuccessText text-sm transition-opacity duration-300",
          {
            "translate-y-0 opacity-0": !formSuccess,
            "translate-y-100 opacity-100": formSuccess,
          }
        )}
      >
        {formSuccess}
      </p>
    </form>
  );
};

export default PromoCode;
